import * as React from "react";
import {Alert, Button, Checkbox, Col, ControlLabel, Form, FormControl, FormGroup, Row} from "react-bootstrap";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Warning} from "../common/model";
import {ActionBar} from "../common/ui/actionbar/ActionBar";
import {CombinedCronPicker, Mode} from "../common/ui/cron/CombinedCronPicker";
import {CronUtil, CronValidationResult} from "../common/ui/cron/CronUtil";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {Spinner} from "../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AOIDialog} from "./AOIDialog";
import {AOIEventsDialog} from "./aoiEvents/AOIEventsDialog";
import {AOIList} from "./AOIList";
import {DeleteAOIButton} from "./DeleteAOIButton";
import {AOINotificationArea, AOIMonitor} from "./model";

interface AOIUserFormOwnProps {
    onClose: () => void;
    onSubmit: () => void;
    refreshData: () => Promise<any>;
    userId: string;
    notification: AOIMonitor;
    browseAOIs: boolean;
}

interface AOIUserFormState {
    error: Error;
    executing: boolean;
    isAOIDialogOpen: boolean;
    isAOIEventsDialogOpen: boolean;
    username: string,
    email: string;
    enabled: boolean;
    schedule: string;
    notificationAreaIndex: number;
    triggers: AOINotificationArea[];
    cronPickerMode: Mode,
}

type AOIUserFormProps = AOIUserFormOwnProps & WithApiProperties & InjectedIntlProps;

const LABEL_COL = 4;
const CONTROL_COL = 8;

class AOIUserFormComponent extends React.Component<AOIUserFormProps, AOIUserFormState> {

    constructor(props: AOIUserFormProps) {
        super(props);

        const notification = props.notification;
        const cronString = notification?.schedule || "0 0 0 * * *";
        let mode: Mode = "simple";
        if (cronString) {
            mode = CombinedCronPicker.canUseSimpleMode(cronString, this.props.intl) ? "simple" : "advanced";
        }

        this.state = {
            executing: false,
            error: null,
            isAOIDialogOpen: false,
            isAOIEventsDialogOpen: false,
            username: notification?.username || "",
            email: notification?.email || "",
            enabled: notification ? notification.enabled : true,
            schedule: cronString,
            notificationAreaIndex: null,
            triggers: [],
            cronPickerMode: mode,
        };
    }

    handleRowSelect = (selection: AOINotificationArea[]) => {
        this.setState({triggers: selection});
    }

    private clearSelection = () => {
        this.handleRowSelect([]);
    }

    validateSchedule = (cronStringToValidate: string): Promise<CronValidationResult> => {
        const validate = (validateCRON) => Promise.resolve(validateCRON);
        return validate(CronUtil.validateCronString(cronStringToValidate, this.props.intl));
    }

    switchMode = (newMode) => {
        if (newMode === "notset") {
            this.setState({...this.state, schedule: undefined, cronPickerMode: newMode});
        } else if (newMode === "simple" &&
                   (!CombinedCronPicker.canUseSimpleMode(this.state.schedule, this.props.intl))) {
            this.setState({...this.state, schedule: this.state.schedule || "0 0 0 * * *", cronPickerMode: newMode});
        } else if (newMode === "advanced" && !this.state.schedule) {
            this.setState({...this.state, schedule: "0 0 0 * * *", cronPickerMode: newMode});
        } else {
            this.setState({...this.state, cronPickerMode: newMode});
        }
    }


    render() {
        const {browseAOIs, userId, notification, onClose} = this.props;
        const {isAOIDialogOpen, isAOIEventsDialogOpen, triggers, notificationAreaIndex} = this.state;
        const noSelection = triggers.length === 0;
        const notificationAreaId = notificationAreaIndex != null && notification.triggers[notificationAreaIndex].id;

        return (
            <Form horizontal>
                {!browseAOIs && <React.Fragment>
                    {this.renderContent()}

                    {!this.state.executing && <FormGroup>
                        <Col smOffset={1} sm={10}>
                            <ActionBar>
                                <Button onClick={this.submit} disabled={!!this.state.error}>
                                    {!notification && <FormattedMessage id="studio.form.create"
                                                                        defaultMessage="Create"/>}
                                    {notification && <FormattedMessage id="studio.form.save"
                                                                       defaultMessage="Save"/>}
                                </Button>
                                <Button onClick={onClose}>
                                    <FormattedMessage id="studio.form.cancel"
                                                      defaultMessage="Cancel"/>
                                </Button>
                            </ActionBar>
                        </Col>
                    </FormGroup>}
                </React.Fragment>}

                {browseAOIs && <React.Fragment>
                    <div>
                        <ActionBar>
                            <div>
                                <Button onClick={this.openAOIDialog}>
                                    <LcdIcon icon={"plus"}/>
                                    <FormattedMessage id="studio.notifications.create-aoi"
                                                      defaultMessage="Create AOI"/>
                                </Button>
                            </div>
                            <div>
                                <DeleteAOIButton disabled={noSelection} data={triggers}
                                                 onDeleteSuccess={this.onDeleteAOISuccess}
                                                 onDelete={this.deleteAOI}
                                                 onDeleteWarnings={this.clearSelection}/>
                            </div>
                        </ActionBar>
                        <AOIList className="public_fixedDataTable_on_drawer" userId={userId}
                                 onRowClick={this.onAOIClick} onRowSelect={this.handleRowSelect}
                                 clearSelection={noSelection}/>
                        {isAOIDialogOpen &&
                            <AOIDialog notification={notification} notificationAreaIndex={notificationAreaIndex}
                                       onSuccess={this.onAOISaved} onHide={this.closeAOIDialog}/>}
                        {isAOIEventsDialogOpen &&
                            <AOIEventsDialog notificationId={notification.id} notificationAreaId={notificationAreaId}
                                             onHide={this.closeAOIEventsDialog}/>}
                    </div>
                    <FormGroup>
                        <Col smOffset={1} sm={10}>
                            <ActionBar>
                                <Button onClick={onClose}>
                                    <FormattedMessage id="studio.ui.form-buttons.close"
                                                      defaultMessage="Close"/>
                                </Button>
                            </ActionBar>
                        </Col>
                    </FormGroup>
                </React.Fragment>}
            </Form>
        );
    }

    private onDeleteAOISuccess = () => {
        this.clearSelection();
        this.props.refreshData();
    };

    private renderContent() {
        if (this.state.executing) {
            return this.renderExecuting();
        }

        if (this.state.error) {
            return this.renderError();
        }

        return (
            <React.Fragment>
                <FormGroup controlId="username">
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.notifications.aoi-user-form.name"
                                          defaultMessage="User Name"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <FormControl name="username" value={this.state.username} onChange={this.onChange}
                                     disabled={!!this.props.notification}/>
                    </Col>
                </FormGroup>

                <FormGroup controlId="email">
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.notifications.aoi-user-form.email"
                                          defaultMessage="Email"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <FormControl name="email" value={this.state.email} onChange={this.onChange}/>
                    </Col>
                </FormGroup>

                <FormGroup controlId="schedule">
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.notifications.aoi-user-form.schedule"
                                          defaultMessage="Schedule"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <CombinedCronPicker value={this.state.schedule}
                                            intl={this.props.intl}
                                            disableNotset={true}
                                            disableMinutes={true}
                                            validate={this.validateSchedule}
                                            mode={this.state.cronPickerMode}
                                            onModeSwitch={this.switchMode}
                                            onChange={(cronString) => {
                                                if (cronString === null) {
                                                    cronString = undefined;
                                                }
                                                this.setState(Object.assign({}, this.state, {schedule: cronString}));
                                            }}/>
                    </Col>
                </FormGroup>

                <FormGroup>
                    <Col smOffset={LABEL_COL} sm={CONTROL_COL}>
                        <Checkbox name="enabled" checked={this.state.enabled} onChange={this.onChange}>
                            <FormattedMessage id="studio.notifications.aoi-user-form.enable-notifications"
                                              defaultMessage="Enable notifications"/>
                        </Checkbox>
                    </Col>
                </FormGroup>
            </React.Fragment>
        );
    }

    private onChange = (event: any) => {
        const {name} = event.target;
        let {value} = event.target;
        value = event.target.type === 'checkbox' ? event.target.checked : value;
        this.setState({[name]: value} as any);
    };

    private onAOIClick = (event, notificationAreaIndex) => {
        if (event.target.dataset.isMainLink) {
            this.setState({isAOIDialogOpen: true, notificationAreaIndex});

            return true;
        }

        if (event.target.dataset.isBrowseEventsLink) {
            this.setState({isAOIEventsDialogOpen: true, notificationAreaIndex});

            return true;
        }
    };

    private openAOIDialog = () => {
        this.setState({isAOIDialogOpen: true});
    };

    private closeAOIDialog = () => {
        this.setState({isAOIDialogOpen: false, notificationAreaIndex: null});
    };

    private closeAOIEventsDialog = () => {
        this.setState({isAOIEventsDialogOpen: false, notificationAreaIndex: null});
    };

    private onAOISaved = () => {
        this.props.refreshData()
            .then(() => {
                this.closeAOIDialog();
            });
    };

    private renderExecuting() {
        const {executing} = this.state;
        if (executing) {
            return (
                <Row style={{textAlign: "center"}}>
                    <h2>
                        <FormattedMessage id="studio.notifications.create-aoi-user-form.saving-aoi-user"
                                          defaultMessage="Saving AOI user..."/>
                    </h2>
                    <Spinner bigStyle/>
                </Row>
            );
        }
        return null;
    }

    private renderError() {
        const {error} = this.state;
        const errorMessage = mapErrorToMessage(error);
        if (errorMessage) {
            return <Alert bsStyle="danger">{errorMessage}</Alert>;
        }
        return <ErrorDisplay error={error}/>;
    }

    private submit = () => {
        const {api, notification, onSubmit} = this.props;

        Promise.resolve()
            .then(() => {

                this.setState({executing: true});

                const monitorToSave = {
                    id: this.props.notification ? this.props.notification.id : null,
                    email: this.state.email,
                    username: this.state.username,
                    schedule: this.state.schedule,
                    enabled: this.state.enabled,
                    triggers: this.props.notification ? this.props.notification.triggers : [],
                };
                if (monitorToSave.id == null) {
                    delete monitorToSave["id"];
                }

                if (this.props.notification) {
                    return api.updateAOINotification({...notification, ...monitorToSave});
                } else {
                    return api.createAOINotification({
                        ...monitorToSave,
                    });
                }
            })
            .catch((error) => {
                this.setState({executing: false, error});

                throw error;
            })
            .then(() => {
                this.setState({executing: false});

                onSubmit();
            });
    };

    private deleteAOI = (): Promise<Warning[]> => {
        const {api, notification} = this.props;
        const {triggers} = this.state;

        return api.deleteAOINotificationArea(notification, triggers[0])
            .then(_r => null);
    };

}

export const AOIUserForm = WithApi(injectIntl(AOIUserFormComponent));